import React, { useState, useEffect, useRef } from 'react';
import NieuwsWidget from '../components/nieuws/NieuwsWidget';
import KoppelingStatusBolletje from '../components/KoppelingStatusBolletje';
import TrialWarning from '../components/TrialWarning';
import TakenWidget from '../components/TakenWidget';
import DashboardReorder from '../components/DashboardReorder';
import WerkorderModal from './WerkorderModal';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLayoutEdit } from '../context/LayoutEditContext';
import { werkordersAPI } from '../services/api';
import './Dashboard.css';
import api from '../services/api';
import FormsInbox from '../components/FormsInbox';

const Dashboard = () => {
  const { gebruiker, hasPermission } = useAuth();
  const { editMode, saving, startEdit, cancelEdit, saveAll } = useLayoutEdit();
  const navigate = useNavigate();
  const [werkorders, setWerkorders] = useState([]);
  const [afdelingen, setAfdelingen] = useState([]);
  const [selectedAfdelingen, setSelectedAfdelingen] = useState([]);
  const [showWerkorderModal, setShowWerkorderModal] = useState(false);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [bedrijfInfo, setBedrijfInfo] = useState(null);
  const dropdownRef = useRef(null);

  const [storingen, setStoringen] = useState([]);
  const [restwerk, setRestwerk] = useState([]);
  const [voorraadTekort, setVoorraadTekort] = useState(null);
  useEffect(() => { api.get('/bussen/dashboard').then(r => setVoorraadTekort((r.data.onder_minimum || []).length + (r.data.bus_tekorten || []).length)).catch(() => {}); }, []);
  const [afgerond, setAfgerond] = useState([]);
  const [monteurLogs, setMonteurLogs] = useState([]);
  const [projecten, setProjecten] = useState([]);
  const [materiaalBestellingen, setMateriaalBestellingen] = useState([]);
  const [onderhoudOverzicht, setOnderhoudOverzicht] = useState([]);
  const [instellingen, setInstellingen] = useState({
    dashboard_laatste_storingen: true,
    dashboard_storing_analyse: true,
    dashboard_restwerk: true,
    dashboard_openstaande_orders: true,
    dashboard_in_behandeling: true,
    dashboard_afgeronde_orders: true,
    dashboard_monteur_logs: true,
    dashboard_projecten: true,
    dashboard_storingen_aantal: 5,
    dashboard_restwerk_aantal: 5,
    dashboard_afgerond_aantal: 5,
    dashboard_monteur_logs_aantal: 5,
    dashboard_openstaande_aantal: 5,
    dashboard_in_behandeling_aantal: 5,
    dashboard_projecten_aantal: 5,
    dashboard_materialen: true,
    dashboard_materialen_aantal: 5,
    dashboard_onderhoud: true
  });

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowFilterDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadData = async () => {
    try {
      const isWerkvoorbereider = hasPermission('dashboard:wv_widgets');

      const promises = [
        api.get('/afdelingen'),
        werkordersAPI.getAll(),
        api.get('/auth/bedrijf-info'),
        api.get('/instellingen')
      ];

      if (isWerkvoorbereider) {
        promises.push(api.get('/klanten/restwerk/overzicht'));
        promises.push(api.get('/storingen'));
        promises.push(api.get("/klanten/logs/recent?limit=10").catch(() => ({ data: { logs: [] } })));
        promises.push(api.get('/projecten').catch(() => ({ data: { projecten: [] } })));
        promises.push(api.get('/materialen/bestellingen?status=te_bestellen').catch(() => ({ data: { bestellingen: [] } })));
        promises.push(api.get('/onderhoud/overzicht').catch(() => ({ data: { data: [] } })));
      }

      const responses = await Promise.all(promises);

      setAfdelingen(responses[0].data.afdelingen || []);
      setBedrijfInfo(responses[2].data.bedrijf);

      if (responses[3].data) {
        setInstellingen(prev => ({ ...prev, ...responses[3].data }));
      }

      const alleOrders = responses[1].data.werkorders;
      const actiefOrders = alleOrders.filter(w => w.status !== 'afgerond');
      const afgerondOrders = alleOrders.filter(w => w.status === 'afgerond').sort((a,b) => new Date(b.afgerond_op || 0) - new Date(a.afgerond_op || 0));

      setWerkorders(actiefOrders);
      setAfgerond(afgerondOrders);

      if (isWerkvoorbereider) {
        setRestwerk(responses[4]?.data?.restwerk || []);
        setMonteurLogs(responses[6]?.data?.logs || []);
        setStoringen(responses[5]?.data?.storingen || []);
        setProjecten(responses[7]?.data?.projecten || responses[6]?.data || []);
        setMateriaalBestellingen(responses[8]?.data?.bestellingen || []);
        setOnderhoudOverzicht(responses[9]?.data?.data || []);
      }

      setLoading(false);
    } catch (error) {
      console.error('Fout bij laden data:', error);
      setLoading(false);
    }
  };

  const getPrioriteitIcon = (prioriteit) => {
    const icons = { 'laag': '🟢', 'normaal': '🟡', 'hoog': '🔴' };
    return icons[prioriteit] || '⚪';
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  const handleAfdelingToggle = (afdelingId) => {
    const idStr = afdelingId.toString();
    if (selectedAfdelingen.includes(idStr)) {
      setSelectedAfdelingen(selectedAfdelingen.filter(id => id !== idStr));
    } else {
      setSelectedAfdelingen([...selectedAfdelingen, idStr]);
    }
  };

  const gefilterdWerkorders = selectedAfdelingen.length > 0
    ? werkorders.filter(w => selectedAfdelingen.includes(w.afdeling_id?.toString()))
    : werkorders;

  const isWerkvoorbereider = hasPermission('dashboard:wv_widgets');
  const [analyse, setAnalyse] = useState(null);
  useEffect(() => {
    if (!hasPermission('analyse:lezen')) return;
    api.get('/analyse/storingen', { params: { periode: 'maand' } })
      .then(r => setAnalyse(r.data.data || null))
      .catch(() => setAnalyse(null));
  }, []);

  let dagenOver = null;
  if (bedrijfInfo && (bedrijfInfo.abonnement === 'trial' || bedrijfInfo.abonnement_type_id === 1)) {
    const eindDatum = new Date(bedrijfInfo.trial_eindigt_op);
    dagenOver = Math.max(0, Math.ceil((eindDatum - new Date()) / (1000 * 60 * 60 * 24)));
  }

  const onderhoudTotaalWO = onderhoudOverzicht.reduce((sum, o) => sum + parseInt(o.aantal_werkorders || 0), 0);
  const onderhoudTotaalAfgerond = onderhoudOverzicht.reduce((sum, o) => sum + parseInt(o.aantal_afgerond || 0), 0);
  const onderhoudNogTeDoen = onderhoudTotaalWO - onderhoudTotaalAfgerond;
  const onderhoudPercentage = onderhoudTotaalWO > 0 ? Math.round((onderhoudTotaalAfgerond / onderhoudTotaalWO) * 100) : 0;

  return (
    <div className="dashboard">
      <TrialWarning dagenOver={dagenOver} />

      {/* Page Header */}
      <div className="page-header">
        <div className="page-header-left">
          <h1>📊 Dashboard</h1>
          <span className="welcome-text">Welkom, {gebruiker?.voornaam ? gebruiker.voornaam.charAt(0).toUpperCase() + gebruiker.voornaam.slice(1) : ""}!</span>
        </div>

        <div className="page-header-right">
          {editMode ? (
            <>
              <button onClick={saveAll} disabled={saving} className="btn-primary" style={{ background: '#16a34a' }}>
                {saving ? '…' : '✓ Opslaan'}
              </button>
              <button onClick={cancelEdit} disabled={saving} className="btn-filter">
                ✗ Annuleren
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setShowWerkorderModal(true)} className="btn-primary">
                + Nieuwe Werkorder
              </button>
              <button onClick={startEdit} className="btn-filter" title="Pas de volgorde van widgets en menu aan">
                ✎ Layout
              </button>
              {afdelingen.length > 1 && (
                <div className="afdeling-filter" ref={dropdownRef}>
                  <button onClick={() => setShowFilterDropdown(!showFilterDropdown)} className="btn-filter">
                    📁 Filter ({selectedAfdelingen.length || 'alle'})
                  </button>
                  {showFilterDropdown && (
                    <div className="filter-dropdown-content">
                      {afdelingen.map(afd => (
                        <label key={afd.id} className="checkbox-label">
                          <input type="checkbox" checked={selectedAfdelingen.includes(afd.id.toString())} onChange={() => handleAfdelingToggle(afd.id)} />
                          <span>{afd.naam}</span>
                        </label>
                      ))}
                      {selectedAfdelingen.length > 0 && (
                        <button onClick={() => setSelectedAfdelingen([])} className="btn-clear-filter">Wis filter</button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="quick-stats">
        <div className="stat-mini" onClick={() => navigate('/openstaand')} style={{cursor:'pointer'}}><span className="stat-num">{gefilterdWerkorders.length}</span><span className="stat-lbl">Actief</span></div>
        <div className="stat-mini stat-nieuw" onClick={() => navigate('/openstaand')} style={{cursor:'pointer'}}><span className="stat-num">{gefilterdWerkorders.filter(w => w.status === 'nieuw' && !w.doorgestuurd_op).length}</span><span className="stat-lbl">Nieuw</span></div>
        <div className="stat-mini" onClick={() => navigate('/doorgestuurd')} style={{cursor:'pointer'}}><span className="stat-num">{werkorders.filter(w => w.status === 'nieuw' && w.doorgestuurd_op && String(w.afdeling_id) === String(gebruiker?.afdeling_id)).length}</span><span className="stat-lbl">📤 Doorgestuurd</span></div>
        <div className="stat-mini stat-behandeling" onClick={() => navigate('/openstaand?status=in_behandeling')} style={{cursor:'pointer'}}><span className="stat-num">{gefilterdWerkorders.filter(w => w.status === 'in_behandeling').length}</span><span className="stat-lbl">In behandeling</span></div>
        <div className="stat-mini" onClick={() => navigate('/openstaand')} style={{cursor:'pointer', background: gefilterdWerkorders.filter(w => w.status === 'lmra_afgekeurd').length > 0 ? '#fef2f2' : ''}}><span className="stat-num" style={{color: gefilterdWerkorders.filter(w => w.status === 'lmra_afgekeurd').length > 0 ? '#dc2626' : ''}}>{gefilterdWerkorders.filter(w => w.status === 'lmra_afgekeurd').length}</span><span className="stat-lbl">⚠️ LMRA</span></div>
        {isWerkvoorbereider && <div className="stat-mini stat-afgerond" onClick={() => navigate('/afgerond')} style={{cursor:'pointer'}}><span className="stat-num">{afgerond.length}</span><span className="stat-lbl">Afgerond</span></div>}
        {isWerkvoorbereider && <div className="stat-mini stat-storing" onClick={() => navigate('/storingen')} style={{cursor:'pointer'}}><span className="stat-num">{storingen.length}</span><span className="stat-lbl">Storingen</span></div>}
        {isWerkvoorbereider && <div className="stat-mini stat-restwerk" onClick={() => navigate('/restwerk')} style={{cursor:'pointer'}}><span className="stat-num">{restwerk.length}</span><span className="stat-lbl">Restwerk</span></div>}
        {isWerkvoorbereider && voorraadTekort !== null && <div className="stat-mini" onClick={() => navigate('/voorraad')} style={{cursor:'pointer'}}><span className="stat-num" style={{ color: voorraadTekort > 0 ? '#dc2626' : undefined }}>{voorraadTekort}</span><span className="stat-lbl">Voorraad</span></div>}
        {isWerkvoorbereider && <div className="stat-mini" onClick={() => navigate('/instellingen')} style={{cursor:'pointer'}}><span className="stat-num" style={{display:'flex',alignItems:'center',justifyContent:'center',height:'1.4em'}}><KoppelingStatusBolletje size={22} /></span><span className="stat-lbl">TMS</span></div>}
      </div>

      {/* Widget Grid */}
      {isWerkvoorbereider && (
        <DashboardReorder>

          <NieuwsWidget />
          {instellingen.dashboard_openstaande_orders && (
            <div data-widget-key="openstaande" className="widget">
              <div className="widget-header">
                <h3>📋 Openstaande Werkorders</h3>
                <span className="widget-count">{gefilterdWerkorders.filter(w => w.status === 'toegewezen').length}</span>
              </div>
              <div className="widget-content">
                {gefilterdWerkorders.filter(w => w.status === 'toegewezen').slice(0, instellingen.dashboard_openstaande_aantal || 5).map(wo => (
                  <div key={wo.id} className="widget-item" onClick={() => navigate(`/werkorder/${wo.id}`)}>
                    <div className="widget-item-title">{getPrioriteitIcon(wo.prioriteit)} {wo.titel}</div>
                    <div className="widget-item-meta">{wo.afdeling_naam} • {formatDate(wo.created_at)}</div>
                  </div>
                ))}
                {gefilterdWerkorders.filter(w => w.status === 'toegewezen').length === 0 && <div className="widget-empty">Geen openstaande werkorders</div>}
              </div>
              <div className="widget-footer" onClick={() => navigate('/openstaand')}>Bekijk alle werkorders →</div>
            </div>
          )}

          {instellingen.dashboard_in_behandeling && (
            <div data-widget-key="behandeling" className="widget">
              <div className="widget-header">
                <h3>🔧 In Behandeling</h3>
                <span className="widget-count">{gefilterdWerkorders.filter(w => w.status === 'in_behandeling').length}</span>
              </div>
              <div className="widget-content">
                {gefilterdWerkorders.filter(w => w.status === 'in_behandeling').slice(0, instellingen.dashboard_in_behandeling_aantal || 5).map(wo => (
                  <div key={wo.id} className="widget-item" onClick={() => navigate(`/werkorder/${wo.id}`)}>
                    <div className="widget-item-title">{getPrioriteitIcon(wo.prioriteit)} {wo.titel}</div>
                    <div className="widget-item-meta">{wo.toegewezen_aan_voornaam || 'Niet toegewezen'} • {wo.afdeling_naam}</div>
                  </div>
                ))}
                {gefilterdWerkorders.filter(w => w.status === 'in_behandeling').length === 0 && <div className="widget-empty">Geen werkorders in behandeling</div>}
              </div>
              {gefilterdWerkorders.filter(w => w.status === 'in_behandeling').length > 5 && <div className="widget-footer" onClick={() => navigate('/afgerond?filter=in_behandeling')}>Bekijk alles ({gefilterdWerkorders.filter(w => w.status === 'in_behandeling').length}) →</div>}
            </div>
          )}

          {instellingen.dashboard_laatste_storingen && (
            <div data-widget-key="storingen" className="widget widget-urgent">
              <div className="widget-header">
                <h3>🚨 Laatste Storingen</h3>
                <span className="widget-count">{storingen.length}</span>
              </div>
              <div className="widget-content">
                {storingen.slice(0, instellingen.dashboard_storingen_aantal || 5).map(st => (
                  <div key={st.id} className="widget-item" onClick={() => navigate(`/storing/${st.id}`)}>
                    <div className="widget-item-title">🔴 {st.titel || st.beschrijving?.substring(0, 40)}</div>
                    <div className="widget-item-meta">{st.melder_naam || 'Onbekend'} • {formatDate(st.created_at)}</div>
                  </div>
                ))}
                {storingen.length === 0 && <div className="widget-empty">Geen storingen</div>}
              </div>
              <div className="widget-footer" onClick={() => navigate('/storingen')}>Bekijk alle storingen →</div>
            </div>
          )}

          {instellingen.dashboard_storing_analyse && analyse && hasPermission('analyse:lezen') && (
            <div data-widget-key="storing_analyse" className="widget">
              <div className="widget-header">
                <h3>📊 Storing-analyse</h3>
                <span className="widget-count">{analyse.totalen ? analyse.totalen.storingen_totaal : 0}</span>
              </div>
              <div className="widget-content">
                <div className="widget-item" style={{cursor:'default'}}>
                  <div className="widget-item-title">
                    {analyse.totalen ? analyse.totalen.storingen_totaal : 0} storingen deze maand
                    {(() => { const v = +(analyse.trendVorige?.aantal ?? 0); const n = analyse.totalen ? +analyse.totalen.storingen_totaal : 0;
                      if (v > 0) { const t = Math.round(((n - v) / v) * 100); return <span style={{ marginLeft: 8, fontWeight: 600, color: t > 0 ? '#dc2626' : '#059669' }}>{t > 0 ? '▲' : '▼'} {Math.abs(t)}%</span>; }
                      return null; })()}
                  </div>
                  <div className="widget-item-meta">Gem. hersteltijd: {analyse.hersteltijdUren?.uren != null ? analyse.hersteltijdUren.uren + ' uur' : '—'}</div>
                </div>
                {(analyse.risicoObjecten || []).slice(0, 3).map((r, i) => (
                  <div key={i} className="widget-item" style={{cursor:'default'}}>
                    <div className="widget-item-title">⚠ {r.object_naam || '(geen object)'}</div>
                    <div className="widget-item-meta">{r.aantal}x gemeld{r.categorie ? ' • ' + r.categorie : ''}</div>
                  </div>
                ))}
                {(analyse.risicoObjecten || []).length === 0 && <div className="widget-empty">Geen risico-objecten deze maand</div>}
              </div>
              <div className="widget-footer" onClick={() => navigate('/storing-analyse')}>Volledige analyse →</div>
            </div>
          )}
          {instellingen.dashboard_restwerk && (
            <div data-widget-key="restwerk" className="widget widget-warning">
              <div className="widget-header">
                <h3>⚠️ Restwerk</h3>
                <span className="widget-count">{restwerk.length}</span>
              </div>
              <div className="widget-content">
                {restwerk.slice(0, instellingen.dashboard_restwerk_aantal || 5).map(rw => (
                  <div key={rw.id} className="widget-item" onClick={() => navigate(`/klant/${rw.klant_id}`)}>
                    <div className="widget-item-title">{rw.klant_naam}</div>
                    <div className="widget-item-meta">{(rw.restwerk_beschrijving || rw.beschrijving)?.substring(0, 50) || 'Geen beschrijving'}</div>
                  </div>
                ))}
                {restwerk.length === 0 && <div className="widget-empty">Geen restwerk</div>}
              </div>
              <div className="widget-footer" onClick={() => navigate('/restwerk')}>Bekijk al het restwerk →</div>
            </div>
          )}

          {instellingen.dashboard_afgeronde_orders && (
            <div data-widget-key="afgerond" className="widget widget-success">
              <div className="widget-header">
                <h3>✅ Recent Afgerond</h3>
                <span className="widget-count">{afgerond.length}</span>
              </div>
              <div className="widget-content">
                {afgerond.slice(0, instellingen.dashboard_afgerond_aantal || 5).map(wo => (
                  <div key={wo.id} className="widget-item" onClick={() => navigate(`/werkorder/${wo.id}`)}>
                    <div className="widget-item-title">✓ {wo.titel}{wo.oa_afgemeld_door && <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: '#fef3c7', color: '#92400e', whiteSpace: 'nowrap' }}>🤝 {wo.oa_afgemeld_door}</span>}</div>
                    <div className="widget-item-meta">{wo.oa_afgemeld_door || wo.toegewezen_aan_voornaam || '-'} • {formatDate(wo.afgerond_op)}</div>
                  </div>
                ))}
                {afgerond.length === 0 && <div className="widget-empty">Geen afgeronde werkorders</div>}
              </div>
              <div className="widget-footer" onClick={() => navigate('/afgerond')}>Bekijk alle afgeronde →</div>
            </div>
          )}

          <div data-widget-key="forms-inbox" className="widget">
              <div className="widget-header">
                <h3>📥 Forms Inbox</h3>
              </div>
              <div className="widget-content">
                <FormsInbox limit={5} compact={true} />
              </div>
              <div className="widget-footer" onClick={() => navigate('/forms/inbox')}>Bekijk alle Forms →</div>
            </div>
            <div data-widget-key="taken">{(instellingen.dashboard_mijn_taken !== false) && <TakenWidget />}</div>

          {instellingen.dashboard_projecten && (
            <div data-widget-key="projecten" className="widget widget-project">
              <div className="widget-header">
                <h3>📁 Projecten</h3>
                <span className="widget-count">{Array.isArray(projecten) ? projecten.length : 0}</span>
              </div>
              <div className="widget-content">
                {Array.isArray(projecten) && projecten.slice(0, instellingen.dashboard_projecten_aantal || 5).map(proj => (
                  <div key={proj.id} className="widget-item" onClick={() => navigate(`/projecten/${proj.id}`)}>
                    <div className="widget-item-title">{proj.naam || proj.titel}</div>
                    <div className="widget-item-meta">
                      <span className="project-progress">{proj.voortgang || 0}%</span>
                      <span> • {proj.werkorders_count || 0} werkorders</span>
                    </div>
                    <div className="progress-bar-mini">
                      <div className="progress-fill" style={{width: `${proj.voortgang || 0}%`}}></div>
                    </div>
                  </div>
                ))}
                {(!Array.isArray(projecten) || projecten.length === 0) && <div className="widget-empty">Geen projecten</div>}
              </div>
              <div className="widget-footer" onClick={() => navigate('/projecten')}>Bekijk alle projecten →</div>
            </div>
          )}

          {instellingen.dashboard_materialen && (
            <div data-widget-key="materialen" className="widget widget-warning">
              <div className="widget-header">
                <h3>📦 Materiaal Bestellingen</h3>
                <span className="widget-count">{materiaalBestellingen.length}</span>
              </div>
              <div className="widget-content">
                {materiaalBestellingen.slice(0, instellingen.dashboard_materialen_aantal || 5).map(b => (
                  <div key={b.id} className="widget-item" style={{ background: '#fef3c7', borderLeft: '3px solid #f59e0b', paddingLeft: 10 }}>
                    <div className="widget-item-title">📦 {b.materiaal_naam}</div>
                    <div className="widget-item-meta">{b.aantal} {b.eenheid} • {b.monteur_naam} • {new Date(b.verzonden_op).toLocaleDateString('nl-NL')}</div>
                  </div>
                ))}
                {materiaalBestellingen.length === 0 && <div className="widget-empty">✅ Geen openstaande bestellingen</div>}
              </div>
              <div className="widget-footer" onClick={() => navigate('/bestellen')}>Bekijk alle bestellingen →</div>
            </div>
          )}

          {instellingen.dashboard_monteur_logs && (
            <div data-widget-key="logs" className="widget widget-info">
              <div className="widget-header">
                <h3>📝 Monteur Logs</h3>
                <span className="widget-count">{monteurLogs.length}</span>
              </div>
              <div className="widget-content">
                {monteurLogs.slice(0, instellingen.dashboard_monteur_logs_aantal || 5).map(log => (
                  <div key={log.id} className="widget-item" onClick={() => navigate(`/klant/${log.klant_id}`)}>
                    <div className="widget-item-title">{log.klant_naam}</div>
                    {log.beschrijving && <div className="widget-item-meta" style={{color: '#475569'}}>{log.beschrijving.substring(0, 60)}{log.beschrijving.length > 60 ? '…' : ''}</div>}
                    <div className="widget-item-meta">{log.monteur_naam} • {log.type}</div>
                  </div>
                ))}
                {monteurLogs.length === 0 && <div className="widget-empty">Geen logs</div>}
              </div>
              <div className="widget-footer" onClick={() => navigate("/monteur-logs")}>Bekijk alle logs →</div>
            </div>
          )}

          {instellingen.dashboard_onderhoud && (
            <div data-widget-key="onderhoud" className="widget">
              <div className="widget-header">
                <h3>🔧 Onderhoud Voortgang</h3>
                <span className="widget-count">{onderhoudOverzicht.length}</span>
              </div>
              <div className="widget-content">
                {onderhoudOverzicht.length > 0 ? (
                  <div>

<div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px" }}>
                      <div style={{ textAlign: "center", flex: 1 }}><div style={{ fontSize: "24px", fontWeight: 700, color: "#3b82f6" }}>{onderhoudTotaalWO}</div><div style={{ fontSize: "11px", color: "#6b7280" }}>Werkorders</div></div>
                      <div style={{ textAlign: "center", flex: 1 }}><div style={{ fontSize: "24px", fontWeight: 700, color: "#10b981" }}>{onderhoudTotaalAfgerond}</div><div style={{ fontSize: "11px", color: "#6b7280" }}>Afgerond</div></div>
                      <div style={{ textAlign: "center", flex: 1 }}><div style={{ fontSize: "24px", fontWeight: 700, color: "#ef4444" }}>{onderhoudNogTeDoen}</div><div style={{ fontSize: "11px", color: "#6b7280" }}>Nog te doen</div></div>
                    </div>
                    <div style={{ background: "#f3f4f6", borderRadius: "8px", padding: "4px" }}>
                      <div style={{ background: onderhoudPercentage > 0 ? "#10b981" : "#e5e7eb", borderRadius: "6px", height: "28px", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: "13px", width: Math.max(onderhoudPercentage, 10) + "%", minWidth: "36px", transition: "width 0.5s" }}>{onderhoudPercentage}%</div>
                    </div>
                  </div>
                ) : <div className="widget-empty">Geen onderhoud ingepland</div>}
              </div>
              <div className="widget-footer" onClick={() => navigate("/onderhoud-overzicht")}>Bekijk onderhoud overzicht →</div>
            </div>
          )}

        </DashboardReorder>
      )}

      <WerkorderModal show={showWerkorderModal} onClose={() => setShowWerkorderModal(false)} onSuccess={loadData} />
    </div>
  );
};

export default Dashboard;
